/**
 * DashboardDateFilter — the standard date-range control for all dashboards.
 *
 *  • Consistent "Date Range" label + quick presets (7D / 30D / This Month)
 *    — no "Today" preset: data is only available up to yesterday (T-1).
 *  • Clean outlined trigger (no yellow tint) that opens the shared calendar popover
 *  • Exports <DateRangeCaption> — a clearly-visible note of the range currently shown
 *
 * Drop the filter into a dashboard's filter bar and the caption near the data so
 * users always know exactly which date range the numbers cover.
 */
import React from 'react'
import {Box, Typography} from '@mui/material'
import EventOutlinedIcon from '@mui/icons-material/EventOutlined'
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined'
import {subDays, startOfMonth, differenceInCalendarDays, isSameDay} from 'date-fns'
import DateRangePicker from '../components/DateRangePicker'
import {formatDate} from '../helpers/UtilityHelper'
import {FONT, SIZE, WEIGHT} from '../styles/foundation/typography'
import {NAVY, BORDER, SUBTLE, WHITE, PRIMARY_BLUE, INFO_BG} from '../styles/foundation/colors'

const now = () => new Date()
// Data is only available up to yesterday (T-1), so every range ends at yesterday.
const yesterday = () => subDays(now(), 1)

// Range presets — each returns a {startDate, endDate} ending at yesterday (T-1).
const PRESETS = [
  {key: '7d', label: 'Last 7 Days', range: () => ({startDate: subDays(now(), 7), endDate: yesterday()})},
  {key: '30d', label: 'Last 30 Days', range: () => ({startDate: subDays(now(), 30), endDate: yesterday()})},
  {key: 'mtd', label: 'This Month', range: () => ({startDate: startOfMonth(now()), endDate: yesterday()})},
]

const cleanTriggerSx = {
  textTransform: 'none',
  fontFamily: FONT,
  fontSize: '0.65rem',
  fontWeight: WEIGHT.semibold,
  color: NAVY,
  bgcolor: WHITE,
  border: `1px solid ${BORDER}`,
  borderRadius: '6px',
  px: 1.25,
  py: 0.4,
  '&:hover': {borderColor: PRIMARY_BLUE, bgcolor: '#f8faff'},
}

export default function DashboardDateFilter({
  dateRange,
  onChange,
  maxDate = subDays(new Date(), 1),
  label = '',
  showPresets = true,
}) {
  const sel = (dateRange && dateRange[0]) || {}

  const isActive = (preset) => {
    const r = preset.range()
    return (
      sel.startDate && sel.endDate &&
      isSameDay(new Date(sel.startDate), r.startDate) &&
      isSameDay(new Date(sel.endDate), r.endDate)
    )
  }

  const applyPreset = (preset) => {
    const r = preset.range()
    // On the 1st of the month, "This Month" start (today) is AFTER the T-1 end (last day of
    // the previous month) — an inverted range that whitescreens downstream charts
    // (eachDayOfInterval throws when start > end). Clamp so end never precedes start.
    if (r.endDate < r.startDate) r.endDate = r.startDate
    onChange([{...r, key: 'selection'}])
  }

  return (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap'}}>
      {label && (
        <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.semibold, color: SUBTLE}}>
          {label}
        </Typography>
      )}

      {showPresets && (
        <Box sx={{display: 'flex', gap: 0.75, flexWrap: 'wrap'}}>
          {PRESETS.map((p) => {
            const on = isActive(p)
            return (
              <Box
                key={p.key}
                onClick={() => applyPreset(p)}
                sx={{
                  px: 1, py: 0.3, borderRadius: '999px', cursor: 'pointer', userSelect: 'none',
                  fontFamily: FONT, fontSize: '0.65rem', fontWeight: WEIGHT.semibold,
                  border: `1px solid ${on ? PRIMARY_BLUE : BORDER}`,
                  bgcolor: on ? PRIMARY_BLUE : WHITE, color: on ? WHITE : NAVY,
                  transition: 'all 0.15s', '&:hover': {borderColor: PRIMARY_BLUE},
                }}
              >
                {p.label}
              </Box>
            )
          })}
        </Box>
      )}

      <DateRangePicker
        defaultRange={dateRange}
        maxDate={maxDate}
        onChange={onChange}
        triggerVariant='outlined'
        btnColor='primary'
        triggerSx={cleanTriggerSx}
      />
    </Box>
  )
}

/**
 * DateRangeCaption — a clearly-visible note of the date range the data covers.
 * Render it near the data on every dashboard so the active range is never ambiguous.
 */
export function DateRangeCaption({dateRange, sx, prefix = 'Showing data for'}) {
  const sel = (dateRange && dateRange[0]) || {}
  if (!sel.startDate || !sel.endDate) return null

  const days = differenceInCalendarDays(new Date(sel.endDate), new Date(sel.startDate)) + 1

  return (
    <Box
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.5,
        bgcolor: INFO_BG, border: `1px solid ${PRIMARY_BLUE}22`,
        borderRadius: '6px', px: 1, py: 0.35, ...sx,
      }}
    >
      <EventOutlinedIcon sx={{fontSize: 13, color: PRIMARY_BLUE}} />
      <Typography sx={{fontFamily: FONT, fontSize: '0.65rem', fontWeight: WEIGHT.medium, color: SUBTLE}}>
        {prefix}{' '}
        <Box component='span' sx={{fontWeight: WEIGHT.bold, color: NAVY}}>
          {formatDate(sel.startDate, 'dd MMM yyyy')} – {formatDate(sel.endDate, 'dd MMM yyyy')}
        </Box>{' '}
        <Box component='span' sx={{color: SUBTLE}}>
          ({days} {days === 1 ? 'day' : 'days'})
        </Box>
      </Typography>
    </Box>
  )
}

/**
 * LiveSnapshotNote — the counterpart to DateRangeCaption for views whose data is a
 * real-time "as of now" view (the underlying endpoints take no date range, e.g.
 * Shipment Alerts, Brand/Courier Health, Stuck Shipments). `title` is a short bold
 * lead that plainly states the time scope; `message` explains what's counted.
 */
export function LiveSnapshotNote({title = 'Current status, updated live', message, sx}) {
  return (
    <Box
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.75,
        bgcolor: INFO_BG, border: `1px solid ${PRIMARY_BLUE}22`,
        borderRadius: '8px', px: 1.5, py: 0.65, ...sx,
      }}
    >
      <ScheduleOutlinedIcon sx={{fontSize: 16, color: PRIMARY_BLUE}} />
      <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.medium, color: SUBTLE}}>
        {title && (
          <>
            <Box component='span' sx={{fontWeight: WEIGHT.bold, color: NAVY}}>{title}</Box>
            {' — '}
          </>
        )}
        {message}
      </Typography>
    </Box>
  )
}
